import * as React from "react";
import { TooltipHost, Stack, IStackTokens, TagPicker, ITag, IBasePickerSuggestionsProps, Label, Spinner, SpinnerSize, FontIcon } from "@fluentui/react";
import { DatasourceService } from "../../services/DatasourceService";
import { connect } from "react-redux";
import { DataSourcePanel } from "./datasourcePanel";
import { INameValuePairIndex } from "../../interfaces/INameValuePairIndex";
import { DatasourceHelper } from "../helpers/dataSourceHelper";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { IDatasourceQueryDto } from "../../interfaces/IDatasourceQueryDto";
import { setDatasourceValues } from "../../redux/modules/datasource";
import { CMAddDatasourceItemPanel } from "../../../../contentManager/Panels/CMAddDatasourceItemPanel";
import { filterSuggestedTagList, sortTerms } from "../helpers/tagHelper";
import { dataSourceItem } from "../../redux/models/reduxModels";

const stackTokens: IStackTokens = {
    childrenGap: 5
};

const getTextFromItem = (item: ITag) => item.name;

const mapDispatchToProps = {
    setDatasourceValues,
};

type reduxDispatch = typeof mapDispatchToProps;

interface ContentTypeNameDatasourcePickerProps extends reduxDispatch {
    contentTypeInputName: string;
    contentTypeNameValue: string;
    datasourceItem: dataSourceItem;
    updateContentTypeNameValue: (newValue: string) => void;
}

export const UnconnectedContentTypeNameDatasourcePicker: React.FunctionComponent<ContentTypeNameDatasourcePickerProps> = ({
    contentTypeInputName,
    contentTypeNameValue,
    datasourceItem,
    updateContentTypeNameValue,
    setDatasourceValues
}: ContentTypeNameDatasourcePickerProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
    const [boundedField, setBoundedField] = React.useState<string>("");
    const dataSourceService = new DatasourceService();
    const dataSourceHelper = new DatasourceHelper(dataSourceService);
    const [selectedTags, setSelectedTags] = React.useState<ITag[]>([]); //selected tags
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [enableAutoFillRelatedFields, setEnableAutoFillRelatedFields] = React.useState<boolean>(false);
    const [isAddDatasourceItemPanelOpen, setIsAddDatasourceItemPanelOpen] = React.useState(false);

    // Newest-run epoch: NewFolderPanel can legitimately SWAP the datasourceItem prop when a
    // client-type verdict settles, so runs of this effect can overlap - a stale run resuming
    // late must not overwrite the new datasource's structure state.
    const loadEpochRef = React.useRef<number>(0);
    React.useEffect(() => {
        const epoch: number = ++loadEpochRef.current;
        if (!datasourceItem) {
            // A truthy -> falsy swap supersedes an in-flight run whose guarded
            // setIsLoading(false) will now be skipped; release the spinner here so it
            // cannot stick. (Unreachable via NewFolderPanel, which unmounts this picker on
            // a falsy item - kept correct for any future consumer.)
            setIsLoading(false);
        }
        if (datasourceItem) {
            (async () => {
                setIsLoading(true);
                try {
                    const result = await dataSourceHelper.getSearchStructure(datasourceItem);
                    if (epoch !== loadEpochRef.current) {
                        return;
                    }
                    // Unconditional (not a one-way latch): a swap to a datasource with fewer
                    // search fields must be able to DISABLE autofill.
                    setEnableAutoFillRelatedFields(Array.isArray(result) && result.length > 1);
                    if (Array.isArray(result)) {
                        setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
                    }
                    const boundedFieldName = DatasourceHelper.getBoundedField(datasourceItem, contentTypeInputName);
                    setBoundedField(boundedFieldName ?? "");
                } catch (error) {
                    logger(error, "contentTypeDatasourcePicker.tsx (useEffect 1)");
                }
                if (epoch === loadEpochRef.current) {
                    setIsLoading(false);
                }
            })();
        }
    }, [datasourceItem]);

    const isFirstRun = React.useRef(true);
    React.useEffect(() => {
        try {
            const newSelectedTag: ITag = contentTypeNameValue
                ? {
                    name: contentTypeNameValue as string,
                    key: contentTypeNameValue as string,
                }
                : null;

            setSelectedTags(newSelectedTag ? [newSelectedTag] : []);

            if (isFirstRun.current) {
                isFirstRun.current = false;
                return;
            }

            updateSearch(newSelectedTag?.key as string ?? "");
        } catch (e) {
            logger(e, "contentTypeDatasourcePicker.tsx (useEffect 2)");
        }
    }, [contentTypeNameValue]);

    const filterSuggestedTags = async (filterText: string, tagList: ITag[]): Promise<ITag[]> => {
        let suggestedTags: ITag[] = [];
        if (filterText?.trim()) {
            try {
                const request: IDatasourceQueryDto = {
                    id: datasourceItem.id,
                    name: datasourceItem.name,
                    tenantId: datasourceItem.tenantId,
                    typeId: datasourceItem.type.id,
                    typeName: datasourceItem.type.name,
                    queryParams: [{ name: boundedField, value: filterText.trim() }],
                    returnOnlyQueriedProperties: true,
                    allowCache: datasourceItem.allowCache
                };
                const result = await dataSourceService.Search(request);
                if (result.length > 0) {
                    const boundFieldProperty: string = Object.keys(result[0]).find(key => key.toLowerCase().trim() === boundedField.toLowerCase().trim());
                    if (boundFieldProperty) {
                        // No "*" browse on this picker - it always narrows by the typed text.
                        suggestedTags = filterSuggestedTagList(
                            result.map<ITag>(resultRow => {
                                const propertyValue = resultRow[boundFieldProperty]?.trim();
                                return {
                                    name: propertyValue,
                                    key: propertyValue
                                }
                            }),
                            filterText, tagList);
                    }
                } else {
                    if (datasourceItem.allowAdd) {
                        suggestedTags.push({
                            name: "Add new item",
                            key: "addNewItem"
                        })
                    }
                }
            } catch (error) {
                logger(error, "datasourceTextTagPicker.tsx (filterSuggestedTags)");
            }
        }
        return sortTerms(filterText, suggestedTags);
    };

    const closePanel = () => {
        setIsOpen(false);
    };

    const updateInputValue = (newValue: string) => {
        updateContentTypeNameValue(newValue);
        updateSearch(newValue);
    };

    const updateSearchInputs = (index: number, value: string) => {
        DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
    };

    const openPanel = async () => {
        updateSearch(contentTypeNameValue);
        setIsOpen(true);
    };

    const updateSearch = (newValue: string) => {
        const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
        updateSearchInputs(index, newValue);
    };

    const onChangeHandle = (tags: ITag[]): void => {
        const searchValue = tags?.length === 1 ? tags[0].key as string : "";
        setSelectedTags(tags);
        updateSearch(searchValue);
        updateContentTypeNameValue(searchValue);
        if (enableAutoFillRelatedFields) getRelatedDatasourceValues(searchValue);
    };

    const getRelatedDatasourceValues = async (searchValue: string) => {
        if (searchValue?.trim()) {
            try {
                // NewFolderPanel can swap the datasourceItem prop when a client-type verdict
                // settles; a Search in flight across that swap must not dispatch the OLD
                // datasource's row into redux.
                const epochAtStart: number = loadEpochRef.current;
                const request: IDatasourceQueryDto = {
                    id: datasourceItem.id,
                    name: datasourceItem.name,
                    tenantId: datasourceItem.tenantId,
                    typeId: datasourceItem.type.id,
                    typeName: datasourceItem.type.name,
                    queryParams: [{ name: boundedField, value: searchValue.trim() }],
                    returnOnlyQueriedProperties: false,
                    allowCache: datasourceItem.allowCache
                };

                const result = await dataSourceService.Search(request);
                if (epochAtStart !== loadEpochRef.current) {
                    return;
                }
                if (result.length === 1) {
                    const propertyKeys = Object.keys(result[0]); //keys of the data source, not the sharepoint columns
                    if (propertyKeys) {
                        let resultItem: any = result[0];
                        if (resultItem) {
                            let boundObject = {};
                            propertyKeys
                                .forEach((key) => {
                                    if (resultItem[key] != null) {
                                        const boundField = datasourceItem?.boundFields?.find(field => {
                                            return field?.datasourceField.toLowerCase().trim() == key.toLowerCase().trim()
                                        }); //check if any of the data source fields have a mapping.
                                        if (boundField) { //if a mapping exists
                                            const names = boundField.name.split(",").map(name => name.toLowerCase().trim());
                                            names
                                                .filter(name => name.toLowerCase().trim() !== contentTypeInputName.toLowerCase().trim())
                                                .forEach(name => {
                                                    boundObject[name] = resultItem[key];
                                                })
                                        } else {
                                            boundObject[key] = resultItem[key];
                                        }
                                    }
                                });

                            setDatasourceValues(boundObject); //redux.    
                        }
                    }
                }
            } catch (error) {
                logger(error, "contentTypeDatasourcePicker.tsx (getRelatedDatasourceValues)");
            }
        }
    }

    const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
        suggestionsHeaderText: languageStrings.Suggestions,
        //noResultsFoundText: showLink ? "No results found, add a new item?" : languageStrings.NoResultsFound
    };

    const onItemSelected = (item) => {
        if (item && item.key === 'addNewItem') {
            setIsAddDatasourceItemPanelOpen(true);
            return null; // return null to prevent adding it to the selected items.
        }
        return item;
    };

    const input = (
        <>
            <Label required>{languageStrings.Name}</Label>
            <TagPicker
                className="xb-sp-input"
                removeButtonAriaLabel="Remove"
                selectedItems={selectedTags}
                onResolveSuggestions={filterSuggestedTags}
                resolveDelay={500}
                getTextFromItem={getTextFromItem}
                pickerSuggestionsProps={pickerSuggestionsProps}
                onChange={onChangeHandle}
                itemLimit={1}
                inputProps={{
                    required: true,
                }}
                onItemSelected={onItemSelected}
            />
        </>
    );

    return (
        <>
            <Stack horizontal verticalAlign="start" tokens={stackTokens}>
                <Stack.Item grow={1}>{input}</Stack.Item>
                {<Stack.Item>
                    {isLoading
                        ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
                        : <div className="xb-search-icon-container">
                            <TooltipHost content={`${languageStrings.Search} ${datasourceItem?.name} ${languageStrings.Datasource}`}>
                                <FontIcon iconName="Search" onClick={openPanel} />
                            </TooltipHost>
                        </div>}
                </Stack.Item>}
            </Stack>
            <DataSourcePanel
                displayName={datasourceItem?.name}
                closePanel={closePanel}
                isOpen={isOpen}
                datasourceItem={datasourceItem}
                datasourceService={dataSourceService}
                searchInputs={searchInputs}
                updateSearchInputs={updateSearchInputs}
                updateInputValue={updateInputValue}
                boundedField={boundedField}
                dataSourceHelper={dataSourceHelper}
                dataSourceItem={datasourceItem}
            />
            {isAddDatasourceItemPanelOpen &&
                <CMAddDatasourceItemPanel
                    panelIsOpen={isAddDatasourceItemPanelOpen}
                    closePanel={() => setIsAddDatasourceItemPanelOpen(false)}
                    displayName={datasourceItem?.name}
                    dataSourceHelper={dataSourceHelper}
                    dataSourceItem={datasourceItem}
                />}
        </>
    );
};

export const ContentTypeNameDatasourcePicker = connect(null, mapDispatchToProps)(UnconnectedContentTypeNameDatasourcePicker);